import React, { useState, useEffect } from 'react';
import { Calculator, TrendingUp, DollarSign, MousePointer, Target, Award, Briefcase, HelpCircle } from 'lucide-react';
import './App.css';

const CalculateurROI = () => {
  const [config] = useState({
    cpc: {
      'Cuisinistes': 1.0,
      'Domotique': 1.5,
      'Isolation : ITE ou ITI': 1.5,
      'Menuiserie/Métallerie': 2.0,
      'Panneaux Photovoltaïques': 3.5,
      'Pergolas': 1.5,
      'Piscines/Spas': 2.5,
      'Poêles/Cheminées': 0.7,
      'Pompes à Chaleur': 1.5,
      'Vérandas': 2.6,
      'Autre Artisan BTP': 2.0
    },
    cvrBase: 6,
    multiplicateurs: {
      'Site internet classique': 0.75,
      'Landing page basique': 1.0,
      'Landing page optimisée': 1.5
    },
    tauxClosing: 15
  });

  const [formData, setFormData] = useState({
    industrie: 'Menuiserie/Métallerie',
    budget: 2000,
    qualitePage: 'Landing page optimisée',
    prixMoyenProjet: 3200
  });

  const [hoveredTooltip, setHoveredTooltip] = useState(null);
  const [resultats, setResultats] = useState(null);

  useEffect(() => {
    calculer();
    // eslint-disable-next-line
  }, [formData, config]);

  const calculer = () => {
    const cpc = config.cpc[formData.industrie];
    const clicsParMois = Math.floor(formData.budget / cpc);
    const multiplicateur = config.multiplicateurs[formData.qualitePage];
    const cvrAjuste = config.cvrBase * multiplicateur;
    const leadsParMois = Math.floor(clicsParMois * (cvrAjuste / 100));
    const cpl = leadsParMois > 0 ? formData.budget / leadsParMois : 0;
    const projetsSignes = Math.floor(leadsParMois * (config.tauxClosing / 100));
    const cpa = projetsSignes > 0 ? formData.budget / projetsSignes : 0;
    const caMensuel = projetsSignes * formData.prixMoyenProjet;
    const caAnnuel = caMensuel * 12;
    const investissementAnnuel = formData.budget * 12;
    const resultatNet = caAnnuel - investissementAnnuel;
    const roi = investissementAnnuel > 0 ? (resultatNet / investissementAnnuel) * 100 : 0;
    const roiParProjet = formData.prixMoyenProjet > 0 && cpa > 0 ? (formData.prixMoyenProjet / cpa).toFixed(1) : 0;

    setResultats({
      cpc,
      clicsParMois,
      cvrAjuste: cvrAjuste.toFixed(1),
      leadsParMois,
      cpl: cpl.toFixed(2),
      projetsSignes,
      cpa: cpa.toFixed(2),
      roiParProjet,
      caMensuel,
      caAnnuel,
      investissementAnnuel,
      resultatNet,
      roi: roi.toFixed(0)
    });
  };

  const Tooltip = ({ id, term, definition }) => (
    <span style={{position: 'relative', display: 'inline-block'}}>
      <HelpCircle 
        size={16}
        style={{
          color: hoveredTooltip === id ? '#4B4BAE' : '#9ca3af',
          cursor: 'help',
          marginLeft: '4px',
          display: 'inline',
          verticalAlign: 'middle'
        }}
        onMouseEnter={() => setHoveredTooltip(id)}
        onMouseLeave={() => setHoveredTooltip(null)}
      />
      {hoveredTooltip === id && (
        <div className="tooltip">
          <div style={{fontWeight: 'bold', marginBottom: '4px', color: '#53e0b4'}}>{term}</div>
          {definition}
        </div>
      )}
    </span>
  );

  return (
    <div className="app-container">
      <div className="container">

    <div style={{textAlign: 'center', marginBottom: '16px'}}>
      <img src="https://yomad.io/wp-content/uploads/2025/10/yomad-logo-dark250x100px.png" alt="YOMAD" style={{height: '60px'}} />
    </div>

        {/* Header */}
        <div className="header">
          <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px'}}>
            <div className="logo-box">
              <Calculator size={28} color="white" />
            </div>
            <div>
              <h1 style={{fontSize: '32px', fontWeight: 'bold', color: '#1f2937', margin: 0}}>
                Calculateur ROI Google Ads
              </h1>
              <p style={{fontSize: '14px', fontWeight: '600', color: '#53e0b4', margin: 0}}>
                YOMAD - Expert Google Ads Artisans
              </p>
            </div>
          </div>
          <p style={{color: '#6b7280', margin: 0}}>
            Découvrez combien de leads qualifiés vous pourriez générer avec votre budget publicitaire
          </p>
        </div>

        {/* Calculateur */}
        <div className="card">
          <div className="grid-2col">
            {/* Formulaire */}
            <div className="form-section">
              <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px'}}>
                <div style={{width: '32px', height: '4px', background: '#53e0b4', borderRadius: '4px'}}></div>
                <h2 style={{fontSize: '24px', fontWeight: 'bold', color: '#1f2937', margin: 0}}>
                  Vos informations
                </h2>
              </div>

              {/* Industrie */}
              <div className="form-group">
                <label className="label">Votre secteur d'activité</label>
                <select
                  value={formData.industrie}
                  onChange={(e) => setFormData({...formData, industrie: e.target.value})}
                  className="select-input"
                >
                  <option>Cuisinistes</option>
                  <option>Domotique</option>
                  <option>Isolation : ITE ou ITI</option>
                  <option>Menuiserie/Métallerie</option>
                  <option>Panneaux Photovoltaïques</option>
                  <option>Pergolas</option>
                  <option>Piscines/Spas</option>
                  <option>Poêles/Cheminées</option>
                  <option>Pompes à Chaleur</option>
                  <option>Vérandas</option>
                  <option>Autre Artisan BTP</option>
                </select>
                <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px'}}>
                  <div style={{width: '8px', height: '8px', background: '#53e0b4', borderRadius: '50%'}}></div>
                  <p style={{fontSize: '12px', color: '#6b7280', margin: 0}}>
                    <span style={{fontWeight: 'bold'}}>CPC</span>
                    <Tooltip 
                      id="cpc-1"
                      term="CPC (Coût Par Clic)"
                      definition="Montant que vous payez à chaque fois qu'un internaute clique sur votre annonce Google Ads. Varie selon votre secteur et la concurrence."
                    />
                    moyen marché : <span style={{fontWeight: 'bold'}}>{config.cpc[formData.industrie]}€</span>
                  </p>
                </div>
              </div>

              {/* Budget */}
              <div className="form-group">
                <label className="label">Budget mensuel Google Ads</label>
                <input
                  type="range"
                  min="500"
                  max="10000"
                  step="100"
                  value={formData.budget}
                  onChange={(e) => setFormData({...formData, budget: parseInt(e.target.value)})}
                  className="range-input"
                  style={{
                    background: `linear-gradient(to right, #4B4BAE 0%, #53e0b4 ${((formData.budget - 500) / 9500) * 100}%, #e5e7eb ${((formData.budget - 500) / 9500) * 100}%, #e5e7eb 100%)`
                  }}
                />
                <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6b7280', marginTop: '8px'}}>
                  <span>500€</span>
                  <span style={{fontWeight: 'bold', fontSize: '24px', color: '#4B4BAE'}}>{formData.budget}€/mois</span>
                  <span>10 000€</span>
                </div>
              </div>

              {/* Qualité page */}
              <div className="form-group">
                <label className="label">Qualité de votre page de destination</label>
                <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                  {Object.keys(config.multiplicateurs).map(qualite => (
                    <label
                      key={qualite}
                      className={`radio-card ${formData.qualitePage === qualite ? 'active' : ''}`}
                    >
                      <input
                        type="radio"
                        name="qualitePage"
                        value={qualite}
                        checked={formData.qualitePage === qualite}
                        onChange={(e) => setFormData({...formData, qualitePage: e.target.value})}
                        style={{accentColor: '#4B4BAE', marginRight: '12px'}}
                      />
                      <div style={{flex: 1}}>
                        <div style={{fontWeight: '600', color: '#1f2937'}}>{qualite}</div>
                        <div style={{fontSize: '12px', color: '#6b7280'}}>
                          <span style={{fontWeight: 'bold'}}>CVR</span>
                          <Tooltip 
                            id={`cvr-${qualite}`}
                            term="CVR (Taux de Conversion)"
                            definition="Pourcentage de visiteurs qui deviennent des leads (remplissent un formulaire, vous appellent). Plus votre page est optimisée, plus le CVR est élevé."
                          />
                           : {(config.cvrBase * config.multiplicateurs[qualite]).toFixed(1)}%
                        </div>
                      </div>
                      {formData.qualitePage === qualite && (
                        <div className="checkmark"></div>
                      )}
                    </label>
                  ))}
                </div>
              </div>

              {/* Prix moyen */}
              <div className="form-group">
                <label className="label">Prix moyen de vos projets</label>
                <div style={{position: 'relative'}}>
                  <span style={{position: 'absolute', left: '16px', top: '12px', color: '#6b7280', fontWeight: 'bold'}}>
                    €
                  </span>
                  <input
                    type="number"
                    value={formData.prixMoyenProjet}
                    onChange={(e) => setFormData({...formData, prixMoyenProjet: parseInt(e.target.value) || 0})}
                    className="number-input"
                    placeholder="3200"
                  />
                </div>
              </div>
              {/* Témoignages - Desktop only */}
              <div className="testimonials-section desktop-only">
                <div className="testimonials-header">
                  <span style={{fontSize: '18px', fontWeight: 'bold', color: '#1f2937'}}>📊 Résultats clients</span>
                </div>
                
                <div className="testimonial-card">
                  <div className="stars">⭐⭐⭐⭐⭐</div>
                  <p className="testimonial-text">"CA +185% et coût par lead divisé par 4. Réactivité et conseils avisés ont fait la différence."</p>
                  <p className="testimonial-author">— Artisan Verrier, Île-de-France</p>
                </div>
                
                <div className="testimonial-card">
                  <div className="stars">⭐⭐⭐⭐⭐</div>
                  <p className="testimonial-text">"On est passé de zéro lead à environ 45 par mois. Le CPL à 45€ est très rentable pour nous, et Ilies est toujours de très bon conseil."</p>
                  <p className="testimonial-author">— Cuisiniste, Grand Est</p>
                </div>
                
                <div className="testimonial-card">
                  <div className="stars">⭐⭐⭐⭐⭐</div>
                  <p className="testimonial-text">"Pour le dépannage, il faut être rapide. On a eu 126 leads rapidement avec un CPL de 20€ ! L'expertise d'Ilies est indéniable."</p>
                  <p className="testimonial-author">— Plombier dépannage, Marseille</p>
                </div>
                
                <div className="testimonials-badge">
                  🏆 Expert Google Ads depuis 2019
                </div>
              </div>
            </div>

            {/* Résultats */}
            {resultats && (
              <div className="results-section">
                <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px'}}>
                  <div style={{width: '32px', height: '4px', background: '#53e0b4', borderRadius: '4px'}}></div>
                  <h2 style={{fontSize: '24px', fontWeight: 'bold', color: '#1f2937', margin: 0}}>
                    Vos résultats prévisionnels
                  </h2>
                </div>

                <div className="metrics-grid">
                  {/* Clics */}
                  <div className="metric-card blue">
                    <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px'}}>
                      <MousePointer size={20} />
                      <span style={{fontSize: '11px', fontWeight: 'bold'}}>CLICS/MOIS</span>
                    </div>
                    <p style={{fontSize: '32px', fontWeight: 'bold', margin: '0 0 4px 0'}}>{resultats.clicsParMois}</p>
                    <p style={{fontSize: '12px', margin: 0, opacity: 0.8}}>à {resultats.cpc}€/clic</p>
                  </div>

                  {/* Leads */}
                  <div className="metric-card green">
                    <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px'}}>
                      <Target size={20} />
                      <span style={{fontSize: '11px', fontWeight: 'bold'}}>LEADS/MOIS</span>
                    </div>
                    <p style={{fontSize: '32px', fontWeight: 'bold', margin: '0 0 4px 0'}}>{resultats.leadsParMois}</p>
                    <p style={{fontSize: '12px', margin: 0, opacity: 0.8}}>CVR : {resultats.cvrAjuste}%</p>
                  </div>

                  {/* CPL */}
                  <div className="metric-card purple">
                    <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px'}}>
                      <DollarSign size={20} />
                      <span style={{fontSize: '11px', fontWeight: 'bold'}}>
                        CPL
                        <Tooltip 
                          id="cpl-1"
                          term="CPL (Coût Par Lead)"
                          definition="Montant que vous coûte chaque demande de contact ou devis. Plus votre page est optimisée, plus le CPL baisse."
                        />
                      </span>
                    </div>
                    <p style={{fontSize: '32px', fontWeight: 'bold', margin: '0 0 4px 0'}}>{resultats.cpl}€</p>
                    <p style={{fontSize: '12px', margin: 0, opacity: 0.8}}>par lead qualifié</p>
                  </div>

                  {/* Projets */}
                  <div className="metric-card orange">
                    <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px'}}>
                      <Award size={20} />
                      <span style={{fontSize: '11px', fontWeight: 'bold'}}>PROJETS SIGNÉS</span>
                    </div>
                    <p style={{fontSize: '32px', fontWeight: 'bold', margin: '0 0 4px 0'}}>{resultats.projetsSignes}/mois</p>
                    <p style={{fontSize: '12px', margin: 0, opacity: 0.8}}>closing : {config.tauxClosing}%</p>
                  </div>

                  {/* CPA */}
                  <div className="metric-card teal full-width">
                    <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px'}}>
                      <Briefcase size={24} />
                      <span style={{fontSize: '13px', fontWeight: 'bold'}}>
                        CPA
                        <Tooltip 
                          id="cpa-1"
                          term="CPA (Coût Par Acquisition)"
                          definition="Montant total que vous investissez en publicité pour obtenir UN projet signé. C'est votre investissement marketing par client."
                        />
                        (COÛT PAR ACQUISITION)
                      </span>
                    </div>
                    <div style={{display: 'flex', alignItems: 'baseline', gap: '12px', marginBottom: '8px'}}>
                      <p style={{fontSize: '40px', fontWeight: 'bold', margin: 0}}>{resultats.cpa}€</p>
                      <p style={{fontSize: '16px', fontWeight: '600', margin: 0}}>par projet signé</p>
                    </div>
                    <p style={{fontSize: '16px', fontWeight: 'bold', margin: 0}}>
                      💰 Chaque projet à {formData.prixMoyenProjet.toLocaleString('fr-FR')}€ vous coûte {resultats.cpa}€ en pub = 
                      <span style={{color: '#53e0b4'}}> ROI de {resultats.roiParProjet}x</span>
                      <Tooltip 
                        id="roi-1"
                        term="ROI (Return On Investment)"
                        definition="Retour sur investissement. Si votre ROI est de 20x, cela signifie que chaque euro investi vous rapporte 20 euros."
                      />
                    </p>
                  </div>
                </div>

                {/* Projection */}
                <div className="projection-card">
                  <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px'}}>
                    <TrendingUp size={24} />
                    <h3 style={{fontSize: '20px', fontWeight: 'bold', margin: 0}}>Projection 12 mois</h3>
                  </div>

                  <div className="chart-container">
                    <div style={{display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', height: '96px', gap: '4px'}}>
                      {Array.from({length: 12}).map((_, i) => (
                        <div
                          key={i}
                          className="chart-bar"
                          style={{height: `${70 + Math.random() * 30}%`}}
                        />
                      ))}
                    </div>
                    <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginTop: '8px', opacity: 0.8}}>
                      <span>Jan</span>
                      <span>Déc</span>
                    </div>
                  </div>

                  <div style={{marginTop: '16px'}}>
                    <div className="projection-row">
                      <span style={{fontSize: '14px', opacity: 0.9}}>CA mensuel estimé</span>
                      <span style={{fontSize: '24px', fontWeight: 'bold'}}>{resultats.caMensuel.toLocaleString('fr-FR')}€</span>
                    </div>

                    <div className="projection-row">
                      <span style={{fontSize: '14px', opacity: 0.9}}>CA annuel estimé</span>
                      <span style={{fontSize: '24px', fontWeight: 'bold'}}>{resultats.caAnnuel.toLocaleString('fr-FR')}€</span>
                    </div>

                    <div style={{fontSize: '14px', opacity: 0.9, margin: '12px 0'}}>
                      <span>Investissement pub annuel</span>
                      <span style={{float: 'right'}}>-{resultats.investissementAnnuel.toLocaleString('fr-FR')}€</span>
                    </div>

                    <div className="projection-row" style={{paddingTop: '12px', borderTop: '2px solid rgba(255,255,255,0.5)'}}>
                      <span style={{fontWeight: '600'}}>Résultat net estimé</span>
                      <span style={{fontSize: '32px', fontWeight: 'bold'}}>{resultats.resultatNet.toLocaleString('fr-FR')}€</span>
                    </div>

                    <div className="roi-box">
                      <p style={{fontSize: '14px', opacity: 0.9, margin: '0 0 4px 0'}}>Retour sur investissement</p>
                      <p style={{fontSize: '64px', fontWeight: 'bold', margin: '0'}}>×{(parseFloat(resultats.roi) / 100 + 1).toFixed(1)}</p>
                      <p style={{fontSize: '24px', fontWeight: '600', margin: '8px 0 0 0'}}>{resultats.roi}% de ROI</p>
                    </div>
                  </div>
                </div>

                {/* CTA */}
                <div className="cta-card">
                  <div style={{textAlign: 'center', marginBottom: '16px'}}>
                    <p style={{fontSize: '24px', fontWeight: 'bold', color: '#1f2937', margin: '0 0 8px 0'}}>
                      🎯 Ces résultats vous intéressent?
                    </p>
                    <p style={{fontSize: '14px', color: '#1f2937', margin: 0}}>
                      Réservez un <span style={{fontWeight: 'bold'}}>appel stratégie de 30 minutes OFFERT</span> pour voir comment mettre ça en place concrètement dans votre entreprise.
                    </p>
                  </div>
                  <button 
                    className="cta-button"
                    onClick={() => window.open('https://calendly.com/yomad/strategie-30min', '_blank')}
                  >
                    🎯 Obtenir ma stratégie personnalisée (appel 30 min gratuit)
                  </button>
                  <p style={{fontSize: '12px', textAlign: 'center', color: '#374151', margin: '12px 0 0 0'}}>
                    ✅ Sans engagement • ✅ Analyse de VOTRE situation • ✅ Plan d'action sur-mesure
                  </p>
                {/* Témoignages - Mobile only */}
                <div className="testimonials-section mobile-only">
                  <div className="testimonials-header">
                    <span style={{fontSize: '18px', fontWeight: 'bold', color: '#1f2937'}}>📊 Résultats clients</span>
                  </div>
                  
                  <div className="testimonial-card">
                    <div className="stars">⭐⭐⭐⭐⭐</div>
                    <p className="testimonial-text">"CA +185% et coût par lead divisé par 4. Réactivité et conseils avisés ont fait la différence."</p>
                    <p className="testimonial-author">— Artisan Verrier, Île-de-France</p>
                  </div>
                  
                  <div className="testimonial-card">
                    <div className="stars">⭐⭐⭐⭐⭐</div>
                    <p className="testimonial-text">"On est passé de zéro lead à environ 45 par mois. Le CPL à 45€ est très rentable pour nous, et Ilies est toujours de très bon conseil."</p>
                    <p className="testimonial-author">— Cuisiniste, Grand Est</p>
                  </div>
                  
                  <div className="testimonial-card">
                    <div className="stars">⭐⭐⭐⭐⭐</div>
                    <p className="testimonial-text">"Pour le dépannage, il faut être rapide. On a eu 126 leads rapidement avec un CPL de 20€ ! L'expertise d'Ilies est indéniable."</p>
                    <p className="testimonial-author">— Plombier dépannage, Marseille</p>
                  </div>
                  
                  <div className="testimonials-badge">
                    🏆 Expert Google Ads depuis 2019
                  </div>
                </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{textAlign: 'center', fontSize: '14px', color: '#6b7280', marginTop: '24px'}}>
          <p style={{fontWeight: 'bold', color: '#4B4BAE', margin: '0 0 4px 0'}}>
            Calculateur ROI Google Ads - YOMAD
          </p>
          <p style={{fontSize: '12px', margin: '4px 0'}}>
            Expert Google Ads pour Artisans BTP | Données benchmarks France 2025
          </p>
          <p style={{fontSize: '12px', margin: '4px 0'}}>
            📧 ilies@yomad.io | 📱 +33 6 32 88 79 75
          </p>
        </div>
      </div>
    </div>
  );
};

export default CalculateurROI;